// src/components/Navbar.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { Bell, Moon, Sun, User as UserIcon, LogOut } from "lucide-react";
import logo from "../assets/voplogo1.webp";

// ✅ Base path for subfolder deployment (/test/vop or full URL via PUBLIC_URL)
const basePath =
  (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) ||
  process.env.PUBLIC_URL ||
  "";

// ✅ Where to send users after sign out
const signOutRedirect = basePath || "/";

function getInitials(nameOrEmail = "") {
  const s = String(nameOrEmail || "").trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** ✅ Dark-mode helper (Tailwind "class" strategy) */
function getInitialTheme() {
  try {
    const saved = localStorage.getItem("theme"); // "light" | "dark" | null
    if (saved === "light" || saved === "dark") return saved;
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
    return prefersDark ? "dark" : "light";
  } catch {
    return "light";
  }
}
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  try {
    localStorage.setItem("theme", theme);
  } catch {
    // ignore
  }
}

export default function Navbar() {
  const { isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { user } = useUser();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false); // mobile nav
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const userMenuRef = useRef(null);

  // theme
  const [theme, setTheme] = useState(() => getInitialTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // ✅ Close menus on route change (prevents stacked overlays on mobile)
  useEffect(() => {
    setIsOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // ✅ Click outside closes user dropdown (desktop + mobile)
  // Use pointerdown + only active when open (prevents "opens then instantly closes" on desktop)
  useEffect(() => {
    function onPointerDown(e) {
      if (!userMenuOpen) return;
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [userMenuOpen]);

  // ✅ ESC closes menus
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        setIsOpen(false);
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const publicLinks = [
    { name: "Home", path: "/" },
    { name: "Login", path: "/login" },
    { name: "Sign Up", path: "/register" },
  ];

  const privateLinks = [
    { name: "Dashboard", path: "/dashboard/learner" },
    { name: "Courses", path: "/courses" },
  ];

  const links = isSignedIn ? privateLinks : publicLinks;

  const linkClasses = (path) =>
    `font-semibold transition-all duration-200 relative group inline-flex items-center ${
      location.pathname === path
        ? "text-orange-600"
        : "text-gray-700 dark:text-slate-200 hover:text-orange-600"
    }`;

  const underlineSpan = (path) => (
    <span
      className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-amber-500 transform origin-left transition-transform duration-200 ${
        location.pathname === path ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
      }`}
    />
  );

  const displayName =
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.primaryEmailAddress?.emailAddress ||
    "User";

  const email = user?.primaryEmailAddress?.emailAddress || "";
  const initials = getInitials(displayName || email);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    // ✅ Make navbar ALWAYS clickable on dashboards (prevents invisible overlays from blocking clicks)
    <nav className="overflow-x-hidden bg-white/95 dark:bg-slate-950/90 backdrop-blur-lg shadow-lg fixed top-0 w-full z-[9999] border-b border-orange-100 dark:border-slate-800 pointer-events-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 min-w-0 pointer-events-auto">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group shrink-0"
          onClick={() => {
            setIsOpen(false);
            setUserMenuOpen(false);
          }}
        >
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full blur opacity-25 group-hover:opacity-40 transition duration-300" />
            <img
              src={logo}
              alt="Voice Of Prophecy Virtual School Logo"
              className="relative h-11 sm:h-12 w-auto transform transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* ✅ prevent overflow by truncating brand text */}
          <div className="hidden sm:flex flex-col min-w-0">
            <span className="text-lg font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent truncate">
              Voice Of Prophecy
            </span>
            <span className="text-xs font-semibold text-gray-600 dark:text-slate-300 -mt-1 truncate">
              Virtual School
            </span>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8 relative z-[10000]">
          {links.map((link) => (
            <Link key={link.path} to={link.path} className={linkClasses(link.path)}>
              {link.name}
              {underlineSpan(link.path)}
            </Link>
          ))}

          {/* ✅ Continue Learning -> /courses */}
          {isSignedIn && (
            <Link
              to="/courses"
              className="shrink-0 inline-flex items-center justify-center px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              Continue Learning
            </Link>
          )}

          {/* ✅ Desktop: replace bell + avatar with Profile + Sign out */}
          {isSignedIn && (
            <div className="flex items-center gap-2">
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-gray-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-slate-900 transition"
              >
                <UserIcon size={18} />
                Profile
              </Link>

              <button
                type="button"
                onClick={() => signOut({ redirectUrl: signOutRedirect })}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:opacity-95 transition"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          )}
        </div>

        {/* Mobile right-side: keep hamburger (and existing mobile user UX) */}
        <div className="md:hidden flex items-center gap-2 shrink-0">
          {isSignedIn && (
            <>
              <button
                type="button"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl hover:bg-orange-50 dark:hover:bg-slate-900 transition"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-700 dark:text-slate-200" />
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-orange-500" />
              </button>

              {/* Mobile avatar (opens userMenuOpen) */}
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen((v) => !v);
                  setIsOpen(false); // ✅ avoid fighting menus
                }}
                className="inline-flex items-center justify-center cursor-pointer pointer-events-auto"
                aria-label="Open user menu"
              >
                {user?.imageUrl ? (
                  <img
                    src={user.imageUrl}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover border border-orange-200 dark:border-slate-700"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center font-bold border border-orange-200 dark:border-slate-700">
                    {initials}
                  </div>
                )}
              </button>
            </>
          )}

          {/* Mobile menu button */}
          <button
            type="button"
            className="inline-flex items-center justify-center p-2 rounded-xl text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-900 focus:outline-none"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
              setUserMenuOpen(false); // ✅ avoid fighting menus
            }}
            aria-label="Open main menu"
          >
            {/* Hamburger */}
            <svg
              className={`h-6 w-6 ${isOpen ? "hidden" : "block"}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>

            {/* X icon */}
            <svg
              className={`h-6 w-6 ${isOpen ? "block" : "hidden"}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* ✅ Mobile dropdown menu (ONLY mobile) */}
      {isOpen && (
        <div className="md:hidden border-t border-orange-100 dark:border-slate-800 bg-white/98 dark:bg-slate-950/95 backdrop-blur-lg pointer-events-auto">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col space-y-2">
            {/* Continue Learning (mobile) */}
            {isSignedIn && (
              <Link
                to="/courses"
                onClick={() => setIsOpen(false)}
                className="w-full inline-flex items-center justify-center px-4 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600"
              >
                Continue Learning
              </Link>
            )}

            {/* Links */}
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="w-full px-4 py-3 rounded-2xl font-semibold text-gray-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-slate-900"
              >
                {link.name}
              </Link>
            ))}

            {/* Theme toggle in mobile menu */}
            {isSignedIn && (
              <button
                type="button"
                onClick={() => toggleTheme()}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 font-semibold"
              >
                <span className="flex items-center gap-2">
                  {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
                  Appearance
                </span>
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-slate-800 dark:text-slate-200">
                  {theme === "dark" ? "Dark" : "Light"}
                </span>
              </button>
            )}

            {/* Profile + Sign out */}
            {isSignedIn && (
              <>
                <Link
                  to="/profile"
                  onClick={() => setIsOpen(false)}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 font-semibold"
                >
                  <UserIcon size={16} />
                  Edit Profile
                </Link>

                <button
                  onClick={() => {
                    setIsOpen(false);
                    signOut({ redirectUrl: signOutRedirect });
                  }}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ✅ Mobile avatar dropdown panel (separate from hamburger menu) */}
      {isSignedIn && userMenuOpen && (
        <div className="md:hidden px-4 pb-2 pointer-events-auto" ref={userMenuRef}>
          <div className="mt-2 w-full bg-white dark:bg-slate-950 rounded-2xl shadow-2xl border border-orange-100 dark:border-slate-800 overflow-hidden">
            <div className="px-4 py-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-slate-900 dark:to-slate-900 border-b border-orange-100 dark:border-slate-800 min-w-0">
              <div className="font-bold text-gray-900 dark:text-slate-100 truncate">{displayName}</div>
              <div className="text-xs text-gray-600 dark:text-slate-300 truncate">{email}</div>
            </div>

            <button
              type="button"
              onClick={() => toggleTheme()}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-slate-900"
            >
              <span className="flex items-center gap-2">
                {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
                Appearance
              </span>
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-slate-800 dark:text-slate-200">
                {theme === "dark" ? "Dark" : "Light"}
              </span>
            </button>

            <Link
              to="/profile"
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-slate-900"
            >
              <UserIcon size={16} />
              Edit Profile
            </Link>

            <div className="h-px bg-orange-100 dark:bg-slate-800" />

            <button
              onClick={() => signOut({ redirectUrl: signOutRedirect })}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 hover:opacity-95"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}